import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { map, Observable } from 'rxjs';
import {
  AiApiEnvelope,
  AiAssistantConfig,
  AiChatResponse,
  AiConfirmResponse,
  AiConfirmValues,
  AiConversationDetail,
  AiConversationSummary,
  AiGreeting,
  AiSuggestion,
} from '../models/ai-assistant.models';

/**
 * Thin HTTP facade over the AI Assistant backend (docs/ai-assistant-api.md).
 *
 * Endpoints (all under `{API_BASE}/api/v1`, office-scoped from the JWT):
 *  - POST `/ai-assistant/chat`            — send a typed/transcribed message
 *  - POST `/ai-assistant/confirm`         — confirm or cancel a pending write
 *  - GET  `/ai-assistant/conversations`   — recent chat history
 *  - GET  `/ai-assistant/conversations/:id` — one transcript + any pending action
 *
 * Returns the unwrapped `data` payload; the `{ status, message, data }` envelope and
 * error mapping are handled by {@link ApiService}.
 */
@Injectable({ providedIn: 'root' })
export class AiAssistantApiService {
  constructor(private readonly api: ApiService) {}

  /**
   * Send a message. Omit `conversationId` to start a new conversation. `images` are base64 data
   * URLs (e.g. `data:image/png;base64,…`) sent for the assistant to read (vision).
   */
  sendMessage(message: string, conversationId?: string | null, images?: string[] | null): Observable<AiChatResponse> {
    const body: Record<string, unknown> = { message };
    if (conversationId) {
      body['conversation_id'] = conversationId;
    }
    if (images?.length) {
      body['images'] = images;
    }
    return this.api.post('/ai-assistant/chat', body).pipe(map((res: AiApiEnvelope<AiChatResponse>) => res?.data));
  }

  /**
   * Confirm (`approve: true`) or cancel (`approve: false`) the pending write action.
   * `values` (the user's inline dropdown picks — lead type/source/status, closing reason, …)
   * is sent only on approve and merged into the action's args server-side before it runs.
   */
  confirmAction(conversationId: string, actionId: string, approve: boolean, values?: AiConfirmValues): Observable<AiConfirmResponse> {
    const body: Record<string, any> = { conversation_id: conversationId, action_id: actionId, approve };
    if (approve && values && Object.keys(values).length) {
      body['values'] = values;
    }
    return this.api.post('/ai-assistant/confirm', body).pipe(map((res: AiApiEnvelope<AiConfirmResponse>) => res?.data));
  }

  /** Timezone-aware greeting + "run through your day?" offer + a quick day snapshot (§0a). */
  getGreeting(): Observable<AiGreeting> {
    return this.api.get('/ai-assistant/greeting').pipe(map((res: AiApiEnvelope<AiGreeting>) => res?.data));
  }

  /** Recent conversations (most recent first, max 50). */
  listConversations(): Observable<AiConversationSummary[]> {
    return this.api.get('/ai-assistant/conversations').pipe(map((res: AiApiEnvelope<AiConversationSummary[]>) => res?.data ?? []));
  }

  /** Load one conversation transcript (plus any still-pending action). */
  getConversation(id: string): Observable<AiConversationDetail> {
    return this.api.get(`/ai-assistant/conversations/${id}`).pipe(map((res: AiApiEnvelope<AiConversationDetail>) => res?.data));
  }

  /** Enabled prompt chips for the welcome screen (FRONTEND_HANDOFF §3). */
  getSuggestions(): Observable<AiSuggestion[]> {
    return this.api.get('/ai-assistant/suggestions').pipe(map((res: AiApiEnvelope<AiSuggestion[]>) => res?.data ?? []));
  }

  /** Suggestions + capabilities (FRONTEND_HANDOFF §8). User-readable; admin to write. */
  getConfig(): Observable<AiAssistantConfig> {
    return this.api.get('/ai-assistant/config').pipe(map((res: AiApiEnvelope<AiAssistantConfig>) => res?.data ?? {}));
  }

  /**
   * Edit suggestions and/or capabilities (admin only — 403 otherwise). Pass `scope: 'global'`
   * (master admin) to edit the global template instead of this office's override.
   */
  updateConfig(payload: AiAssistantConfig, scope?: 'global'): Observable<AiAssistantConfig> {
    const path = scope === 'global' ? '/ai-assistant/config?scope=global' : '/ai-assistant/config';
    return this.api.put(path, payload).pipe(map((res: AiApiEnvelope<AiAssistantConfig>) => res?.data ?? {}));
  }
}
